// File: /components/SpeechToTextButton.tsx
import { Editor } from '@tiptap/react'
import React, { useCallback, useEffect, useState } from 'react'
import SpeechRecognition, {
	useSpeechRecognition,
} from 'react-speech-recognition'

import { CircularProgress } from '@mui/material'
import classNames from 'classnames'

import AudioTranscriptionIcon from 'assets/images/AudioTranscription.svg'
import AudioTranscriptionRecordingIcon from 'assets/images/AudioTranscriptionRecording.svg'

import './styles.css'

export interface Props {
	editor: Editor | null
}

const AudioTranscriptionButton: React.FC<Props> = ({ editor }) => {
	const { transcript, interimTranscript, resetTranscript } =
		useSpeechRecognition()
	const [isTranscribing, setTranscribing] = React.useState(false)
	const [completeTranscript, setCompleteTranscript] = useState('')
	const [lastInterimLength, setLastInterimLength] = useState(0)
	const [isLoading, setIsLoading] = useState(false)

	const findOverlap = (str1: string, str2: string): number => {
		const words = str1.trim().split(' ')
		for (let i = words.length; i >= 0; i--) {
			const suffix = words.slice(i).join(' ')
			if (suffix && str2.startsWith(suffix)) return suffix.length
		}
		return 0
	}

	const updateEditorContent = useCallback(
		(content: string) => {
			if (!editor) return

			if (lastInterimLength > 0) {
				const currentPos = editor.state.selection.from
				const deleteFrom = Math.max(0, currentPos - lastInterimLength)
				editor
					.chain()
					.focus()
					.deleteRange({ from: deleteFrom, to: currentPos })
					.run()
			}
			editor.chain().focus().insertContent(content).run()
		},
		[editor, lastInterimLength]
	)

	useEffect(() => {
		if (!interimTranscript || !editor) return

		const isReset = interimTranscript.length < lastInterimLength
		const overlap = isReset
			? findOverlap(completeTranscript, interimTranscript)
			: 0
		const newContent = isReset
			? completeTranscript + interimTranscript.slice(overlap)
			: completeTranscript +
				interimTranscript.slice(completeTranscript.length)

		if (newContent !== completeTranscript) {
			setCompleteTranscript(newContent)
			updateEditorContent(newContent)
			setLastInterimLength(newContent.length)
		}
	}, [
		interimTranscript,
		editor,
		completeTranscript,
		lastInterimLength,
		updateEditorContent,
	])

	useEffect(() => {
		if (transcript && transcript !== completeTranscript) {
			setCompleteTranscript(transcript)
		}
	}, [transcript, completeTranscript])

	if (
		!SpeechRecognition.browserSupportsSpeechRecognition ||
		editor === null
	) {
		return (
			<span data-testid="audio-transcription-not-supported">
				Browser does not support speech recognition.
			</span>
		)
	}

	const handleListen = () => {
		if (isTranscribing) {
			SpeechRecognition.stopListening()
			setTranscribing(false)
			if (editor) {
				resetTranscript()
				setLastInterimLength(0)
				setCompleteTranscript('')
			}
		} else {
			resetTranscript()
			setLastInterimLength(0)
			setCompleteTranscript('')
			setIsLoading(true)
			SpeechRecognition.startListening({
				continuous: true,
				interimResults: true,
			})
			setTimeout(() => {
				setTranscribing(true)
				setIsLoading(false)
			}, 1000)
		}
	}

	return (
		<div className="audio-transcription-wrapper">
			{isLoading ? (
				<div>
					<CircularProgress size={15} sx={{ marginLeft: '1rem' }} />
				</div>
			) : (
				<button
					className={classNames('menu-button', {
						'is-active': isTranscribing,
					})}
					onClick={handleListen}
					aria-label="audio-transcription-icon"
					data-testid="audio-transcription-icon"
				>
					{isTranscribing ? (
						<div>
							<img
								src={
									AudioTranscriptionRecordingIcon as unknown as string
								}
								alt="transcription icon on"
							/>
						</div>
					) : (
						<img
							src={AudioTranscriptionIcon as unknown as string}
							alt="transcription icon off"
						/>
					)}
				</button>
			)}
			{isTranscribing && (
				<span className={`transcribing-text`}>Transcribing...</span>
			)}
		</div>
	)
}

export default AudioTranscriptionButton
